
import { supabase } from '@/integrations/supabase/client';
import { format, addDays, parseISO, differenceInHours } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { fetchTeams } from './scheduleUtils';

export const generateShiftAlerts = async (weekStartDate: Date, teamId: string | null = null) => {
  const weekEndDate = addDays(weekStartDate, 6);
  const formattedStartDate = format(weekStartDate, 'yyyy-MM-dd');
  
  console.log(`Generating shift alerts for week starting ${formattedStartDate}`);
  
  try {
    // Fetch teams (either specific team or all teams)
    const teams = await fetchTeams(teamId);
    
    if (teams.length === 0) {
      console.log('No teams found to process');
      return [];
    }
    
    const alerts = [];
    
    // Process each team to check for unassigned schedules
    for (const team of teams) {
      console.log(`Checking shifts for team: ${team.name} (${team.id})`);
      
      // Fetch team schedules for the week
      const { data: schedules, error } = await supabase
        .from('team_schedules')
        .select('*')
        .eq('team_id', team.id)
        .gte('start_date', format(weekStartDate, 'yyyy-MM-dd'))
        .lte('end_date', format(weekEndDate, 'yyyy-MM-dd'));
        
      if (error) {
        console.error(`Error fetching schedules for team ${team.name}:`, error);
        continue;
      }
      
      // Check for unassigned schedules (no guard assigned)
      const unassignedSchedules = schedules.filter(
        schedule => !schedule.assigned_guards || schedule.assigned_guards.length === 0
      );
      
      if (unassignedSchedules.length > 0) {
        console.log(`Found ${unassignedSchedules.length} unassigned schedules for team ${team.name}`);
        
        // Create an alert for unassigned schedules
        alerts.push({
          team_id: team.id,
          team_name: team.name,
          alert_type: 'UNASSIGNED_SCHEDULE',
          alert_message: `Team ${team.name} has ${unassignedSchedules.length} unassigned shifts for the week starting ${formattedStartDate}.`,
          week_start_date: formattedStartDate,
          created_at: new Date().toISOString(),
          is_resolved: false,
          content: `${unassignedSchedules.length} shifts don't have guards assigned.`
        });
      }
    }
    
    console.log(`Generated ${alerts.length} unassigned shift alerts`);
    return alerts;
  } catch (error) {
    console.error('Error generating shift alerts:', error);
    throw error;
  }
};

// Check if a shift sequence has a night shift followed by a morning shift
export const checkNightMorningShiftSequence = (
  guardId: string,
  guardName: string,
  schedules: any[]
): { hasIssue: boolean; nightShift: any | null; morningShift: any | null } => {
  // Sort schedules chronologically
  const sortedSchedules = [...schedules].sort((a, b) => 
    fromLocalISOString(a.start_date).getTime() - fromLocalISOString(b.start_date).getTime()
  );
  
  // Initialize result
  const result = {
    hasIssue: false,
    nightShift: null,
    morningShift: null
  };
  
  // Check for consecutive shifts
  for (let i = 0; i < sortedSchedules.length - 1; i++) {
    const currentShift = sortedSchedules[i];
    const nextShift = sortedSchedules[i + 1];
    
    // Skip if either shift doesn't have the guard assigned
    if (!currentShift.assigned_guards?.includes(guardId) || 
        !nextShift.assigned_guards?.includes(guardId)) {
      continue;
    }
    
    // Check if current shift is a night shift (ends after 22:00)
    const currentEndHour = fromLocalISOString(currentShift.end_date).getHours();
    const isNightShift = currentEndHour >= 22 || currentEndHour < 6;
    
    // Check if next shift is a morning shift (starts before 8:00)
    const nextStartHour = fromLocalISOString(nextShift.start_date).getHours();
    const isMorningShift = nextStartHour < 8;
    
    // If we have a night-morning sequence, record it
    if (isNightShift && isMorningShift) {
      // Check if they are on consecutive days
      const currentDate = fromLocalISOString(currentShift.start_date).toDateString();
      const nextDate = fromLocalISOString(nextShift.start_date).toDateString();
      
      if (currentDate !== nextDate) {
        console.log(`[NIGHT-MORNING] ${guardName} has a night shift followed by a morning shift`);
        
        result.hasIssue = true;
        result.nightShift = currentShift;
        result.morningShift = nextShift;
        
        break;
      }
    }
  }
  
  return result;
};

// Generate content for night-morning shift sequence alert
export const generateNightMorningShiftAlertContent = (
  guardName: string,
  teamName: string,
  nightShift: any,
  morningShift: any
): string => {
  const nightShiftStart = format(fromLocalISOString(nightShift.start_date), 'MMM dd, HH:mm');
  const nightShiftEnd = format(fromLocalISOString(nightShift.end_date), 'HH:mm');
  
  const morningShiftStart = format(fromLocalISOString(morningShift.start_date), 'MMM dd, HH:mm');
  const morningShiftEnd = format(fromLocalISOString(morningShift.end_date), 'HH:mm');
  
  return `${guardName} in team ${teamName} has a night shift (${nightShiftStart} - ${nightShiftEnd}) followed by a morning shift the next day (${morningShiftStart} - ${morningShiftEnd}), which may cause fatigue.`;
};

// Check for insufficient rest between shifts
export const checkInsufficientRestBetweenShifts = (
  guardId: string,
  guardName: string,
  schedules: any[]
): { hasIssue: boolean; firstShift: any | null; secondShift: any | null; restHours?: number } => {
  // Sort schedules chronologically
  const sortedSchedules = [...schedules].sort((a, b) => 
    fromLocalISOString(a.start_date).getTime() - fromLocalISOString(b.start_date).getTime()
  );
  
  // Initialize result
  const result = {
    hasIssue: false,
    firstShift: null,
    secondShift: null,
    restHours: undefined
  };
  
  // Check for consecutive shifts with less than 11 hours rest
  for (let i = 0; i < sortedSchedules.length - 1; i++) {
    const currentShift = sortedSchedules[i];
    const nextShift = sortedSchedules[i + 1];
    
    // Skip if either shift doesn't have the guard assigned
    if (!currentShift.assigned_guards?.includes(guardId) || 
        !nextShift.assigned_guards?.includes(guardId)) {
      continue;
    }
    
    // Calculate hours between end of current shift and start of next shift
    const currentEnd = fromLocalISOString(currentShift.end_date);
    const nextStart = fromLocalISOString(nextShift.start_date);
    
    const restTimeHours = differenceInHours(nextStart, currentEnd);
    
    // If rest time is less than 11 hours, record it
    if (restTimeHours < 11) {
      console.log(`[INSUFFICIENT REST] ${guardName} has only ${restTimeHours} hours between shifts`);
      
      result.hasIssue = true;
      result.firstShift = currentShift;
      result.secondShift = nextShift;
      result.restHours = restTimeHours;
      
      break;
    }
  }
  
  return result;
};

// Generate content for insufficient rest alert
export const generateInsufficientRestAlertContent = (
  guardName: string,
  teamName: string,
  firstShift: any,
  secondShift: any,
  restHours: number
): string => {
  const firstShiftStart = format(fromLocalISOString(firstShift.start_date), 'MMM dd, HH:mm');
  const firstShiftEnd = format(fromLocalISOString(firstShift.end_date), 'HH:mm');
  
  const secondShiftStart = format(fromLocalISOString(secondShift.start_date), 'MMM dd, HH:mm');
  const secondShiftEnd = format(fromLocalISOString(secondShift.end_date), 'HH:mm');
  
  return `${guardName} in team ${teamName} has only ${restHours.toFixed(2)} hours of rest between shifts (${firstShiftStart} - ${firstShiftEnd} and ${secondShiftStart} - ${secondShiftEnd}). A minimum of 11 hours is recommended.`;
};
